import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPinIcon,
  CurrencyDollarIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  ClockIcon,
  AcademicCapIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { FaLinkedinIn } from 'react-icons/fa';
import ApplicationForm from '../components/ApplicationForm';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const generateSignature = (apiSecret, method, path, timestamp) => {
  const payload = timestamp + method + path; // Usando Web Crypto API para generar HMAC-SHA256

  const encoder = new TextEncoder();
  const key = encoder.encode(apiSecret);
  const message = encoder.encode(payload);

  return crypto.subtle
    .importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
    .then((cryptoKey) => {
      return crypto.subtle.sign('HMAC', cryptoKey, message);
    })
    .then((signature) => {
      // Convertir ArrayBuffer a hex string
      return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    });
};

const JobDetailsPage = () => {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [isJobActive, setIsJobActive] = useState(true);
  const [isJobExpired, setIsJobExpired] = useState(false); // Nuevo estado para vacante vencida

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);

        const timestamp = Math.floor(Date.now() / 1000).toString();

        const signatureJobDetailsPage = await generateSignature(
          process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
          'GET', // Ejemplo: 'GET'
          `/vacantes/slug/${slug}`, // Ejemplo: '/categories/with_counts/'
          timestamp
        );

        const response = await fetch(`${API_BASE_URL}/vacantes/slug/${slug}`, {
          headers: {
            'X-Timestamp': timestamp,
            'X-Signature': signatureJobDetailsPage,
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Vacante no encontrada');
          }
          throw new Error('Error al cargar los detalles de la vacante');
        }

        const data = await response.json();
        setJob(data);

        // Verificar si la vacante está activa basándose en el campo "status"
        const statusField = data.status || data.estado_vacante || data.activo;
        const isActive =
          statusField === 'activo' ||
          statusField === 'active' ||
          statusField === true;
        setIsJobActive(isActive);

        // Verificar si la vacante ha vencido comparando la fecha actual con finaliza_vacante
        if (data.finaliza_vacante) {
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Normalizar a inicio del día

          const expirationDate = new Date(data.finaliza_vacante);
          expirationDate.setHours(0, 0, 0, 0); // Normalizar a inicio del día

          // Si la fecha actual es posterior a la fecha de vencimiento
          const hasExpired = today > expirationDate;
          setIsJobExpired(hasExpired);
        }
      } catch (error) {
        setError(error.message);
        console.error('Error fetching job details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [slug]);

  // Prevenir scroll cuando el modal está abierto
  useEffect(() => {
    if (showApplicationForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showApplicationForm]);

  const formatDate = (dateString) => {
    if (!dateString) return 'No especificada';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  const parseListItems = (text) => {
    if (!text) return [];
    return text
      .split('-')
      .filter((item) => item.trim())
      .map((item) => item.trim());
  };

  const getLanguageText = (key, lang) => {
    const translations = {
      profile: { es: 'Perfil', en: 'Profile' },
      activities: { es: 'Actividades', en: 'Activities' },
      weOffer: { es: 'Ofrecemos', en: 'We Offer' },
      workplace: { es: 'Lugar de trabajo', en: 'Workplace' },
      jobInfo: { es: 'Información del empleo', en: 'Job Info' },
      companyInfo: { es: 'Información de la empresa', en: 'Company Info' },
      apply: { es: '¡Póstulate a esta vacante!', en: 'Apply for this Job!' },
      send: { es: 'Enviar', en: 'Send' },
    };
    return translations[key]?.[lang === 'ingles' ? 'en' : 'es'] || key;
  };

  // Función para consolidar áreas bilingües (igual que en JobListing)
  const consolidateArea = (area) => {
    if (!area) return area;

    const areaLower = area.toLowerCase().trim();

    // Consolidar Ventas/Sales
    if (areaLower === 'sales' || areaLower === 'ventas') {
      return 'Ventas';
    }

    // Consolidar Operations/Operaciones
    if (areaLower === 'operations' || areaLower === 'operaciones') {
      return 'Operaciones';
    }

    // Consolidar Tesorería
    if (areaLower === 'tesoreria' || areaLower === 'tesorería') {
      return 'Tesorería';
    }

    // Consolidar Atracción de Talento/Recruitment
    if (
      areaLower === 'recruitment' ||
      areaLower === 'atracción de talento' ||
      areaLower === 'atraccion de talento'
    ) {
      return 'Atracción de Talento';
    }

    // Consolidar Comercial/Commercial
    if (areaLower === 'commercial' || areaLower === 'comercial') {
      return 'Comercial';
    }

    // Mantener el nombre original pero con formato consistente
    return area.charAt(0).toUpperCase() + area.slice(1).toUpperCase();
  };

  // Función para obtener el color de fondo según la categoría
  const getCategoryBackground = (area) => {
    const consolidatedArea = consolidateArea(area);

    switch (consolidatedArea) {
      case 'Ventas':
        return 'bg-blue-600'; // Azul para Ventas
      case 'Operaciones':
        return 'bg-orange-600'; // Naranja para Operaciones
      case 'Tesorería':
        return 'bg-green-600'; // Verde para Tesorería
      case 'Atracción de Talento':
        return 'bg-purple-600'; // Morado para Atracción de Talento
      case 'Comercial':
        return 'bg-teal-600'; // Verde azulado para Comercial
      default:
        return 'bg-primary-blue'; // Color por defecto
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-orange mx-auto"></div>
          <p className="mt-4 text-gray-600">
            Cargando detalles de la vacante...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-10 h-10 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">{error}</h3>
          <p className="text-gray-600 mb-6">
            La vacante que buscas no existe o ha sido eliminada.
          </p>
          <Link to="/vacantes" className="btn-primary">
            Ver todas las vacantes
          </Link>
        </div>
      </div>
    );
  }

  if (!job) return null;

  const language = job.idioma_vacante || 'español';
  const perfilItems = parseListItems(job.perfil);
  const actividadesItems = parseListItems(job.actividades);
  const ofrecemosItems = parseListItems(job.ofrecemos);

  // Obtener el color de fondo según la categoría
  const categoryBgClass = getCategoryBackground(job.area_departamento);

  // Determinar el mensaje de estado de la vacante
  const getJobStatusMessage = () => {
    if (!isJobActive) {
      return {
        title: 'Vacante inactiva',
        message: 'Esta vacante se encuentra inactiva o ha sido cerrada.',
        icon: 'inactive',
      };
    }

    if (isJobExpired) {
      return {
        title: 'Vacante vencida',
        message: `Esta vacante venció el ${formatDate(
          job.finaliza_vacante
        )}. Ya no está recibiendo aplicaciones.`,
        icon: 'expired',
      };
    }

    return null;
  };

  const jobStatus = getJobStatusMessage();
  const canApply = isJobActive && !isJobExpired;

  return (
    <>
      {/* Modal de Aplicación - Solo mostrar si la vacante está activa y no ha vencido */}
      {showApplicationForm && canApply && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header del Modal */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-8 py-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Postulación: {job.titulo_vacante}
                </h2>
                <p className="text-gray-600 mt-1">
                  Completa el formulario para aplicar a esta posición
                </p>
              </div>
              <button
                onClick={() => setShowApplicationForm(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Cerrar formulario"
              >
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Contenido del Modal */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="px-8 py-6">
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    Información de la vacante
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-gray-700">
                        Empresa: {job.nombre_empresa || 'Packsys'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <MapPinIcon className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-gray-700">
                        Ubicación: {job.localidad}, {job.estado}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-gray-700">
                        Salario: ${job.salario} {job.moneda} {job.tipo_salario}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <BriefcaseIcon className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="text-gray-700">
                        Tipo: {job.tipo_empleo}
                      </span>
                    </div>
                  </div>
                </div>

                <ApplicationForm
                  jobDetails={job}
                  onClose={() => setShowApplicationForm(false)}
                />
              </div>
            </div>

            {/* Footer del Modal */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-8 py-4">
              <div className="flex justify-between items-center">
                <button
                  onClick={() => setShowApplicationForm(false)}
                  className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium"
                >
                  Cancelar
                </button>
                <p className="text-sm text-gray-500 text-center">
                  Al enviar tu aplicación, aceptas nuestros términos y
                  condiciones
                </p>
                <div className="w-24"></div> {/* Spacer para alineación */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenido Principal de la Página */}
      <div className="min-h-screen bg-gray-50 pt-4 lg:pt-6">
        {/* Hero Banner con fondo personalizado por categoría - Versión con difuminado ajustado al 50% */}
        <div className="relative overflow-hidden">
          {/* Fondo principal */}
          <div className="absolute inset-0 flex">
            {/* Imagen de fondo - ocupa el 50% derecho */}
            <div className="w-[45%]"></div> {/* Espacio para el gradiente */}
            <div
              className="w-4/5 bg-cover bg-top"
              style={{
                backgroundImage: "url('/assets/banners/banner-ventas.png')",
              }}
            >
              {/* Capa de opacidad oscura sobre la imagen */}
              <div className="absolute inset-0 bg-black opacity-20"></div>
            </div>
            {/* Gradiente que ocupa el 50% izquierdo */}
            <div
              className="absolute inset-y-0 left-0 w-1/2"
              style={{
                background: `linear-gradient(90deg, 
          ${
            categoryBgClass.includes('blue')
              ? '#1243adff'
              : categoryBgClass.includes('green')
                ? '#18803eff'
                : categoryBgClass.includes('orange')
                  ? 'rgb(194, 68, 0)'
                  : categoryBgClass.includes('purple')
                    ? 'rgb(108, 23, 182)'
                    : categoryBgClass.includes('teal')
                      ? '#0a7472'
                      : '#1243adff'
          } 0%, 
          ${
            categoryBgClass.includes('blue')
              ? '#2563eb'
              : categoryBgClass.includes('green')
                ? '#16a34a'
                : categoryBgClass.includes('orange')
                  ? '#fc6c1f'
                  : categoryBgClass.includes('purple')
                    ? '#ac47ff'
                    : categoryBgClass.includes('teal')
                      ? '#1eb3b0'
                      : '#2563eb'
          } 80%, 
          rgba(0,0,0,0) 100%)`,
              }}
            ></div>
          </div>

          {/* Contenido sobre el fondo */}
          <div className="relative z-10">
            <div className="container mx-auto px-4 py-12">
              <div className="max-w-4xl">
                <div className="flex items-center space-x-2 text-white/80 mb-4 ">
                  <Link to="/vacantes" className="hover:text-white">
                    Vacantes
                  </Link>
                  <span>/</span>
                  <span>{consolidateArea(job.area_departamento)}</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                  {job.titulo_vacante}
                </h1>

                {/* Mostrar indicador si la vacante no está disponible */}
                {jobStatus && (
                  <div
                    className={`inline-flex items-center px-4 py-2 rounded-lg mb-4 ${
                      jobStatus.icon === 'expired'
                        ? 'bg-red-600/90 text-white'
                        : 'bg-yellow-600/90 text-white'
                    }`}
                  >
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                    <span>{jobStatus.title}</span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-4 text-white/90">
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="h-5 w-5" />
                    <span>{job.nombre_empresa || 'Packsys'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="h-5 w-5" />
                    <span>
                      {job.localidad}, {job.estado}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-5 w-5" />
                    <span>Publicada: {formatDate(job.fecha_posteo)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Job Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Quick Info Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <CurrencyDollarIcon className="h-6 w-6 text-primary-blue" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Salario</p>
                      <p className="font-semibold">
                        ${job.salario} {job.moneda} {job.tipo_salario}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <BriefcaseIcon className="h-6 w-6 text-primary-orange" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Tipo de empleo</p>
                      <p className="font-semibold">{job.tipo_empleo}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <ClockIcon className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Horario</p>
                      <p className="font-semibold">{job.horario}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <AcademicCapIcon className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Estudios</p>
                      <p className="font-semibold">{job.grado_estudios}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-4">Descripción</h2>
                <p className="text-gray-600 leading-relaxed">
                  {job.descripcion_corta}
                </p>
              </div>

              {/* Perfil */}
              {perfilItems.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6">
                    {getLanguageText('profile', language)}
                  </h2>
                  <ul className="space-y-3">
                    {perfilItems.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actividades */}
              {actividadesItems.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6">
                    {getLanguageText('activities', language)}
                  </h2>
                  <ul className="space-y-3">
                    {actividadesItems.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircleIcon className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Ofrecemos */}
              {ofrecemosItems.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6">
                    {getLanguageText('weOffer', language)}
                  </h2>
                  <ul className="space-y-3">
                    {ofrecemosItems.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircleIcon className="h-6 w-6 text-orange-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Responsabilidades */}
              {job.responsabilidades && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6">Responsabilidades</h2>
                  <div className="text-gray-600 whitespace-pre-line">
                    {job.responsabilidades}
                  </div>
                </div>
              )}

              {/* Lugar de trabajo */}
              {(job.lugar_trabajo || job.url_ubicacion) && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6">
                    {getLanguageText('workplace', language)}
                  </h2>
                  <div className="space-y-4">
                    {job.lugar_trabajo && (
                      <div>
                        <h3 className="font-semibold mb-2">Ubicación</h3>
                        {job.url_ubicacion ? (
                          <a
                            href={job.url_ubicacion}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-blue hover:text-primary-orange flex items-center gap-2"
                          >
                            {job.lugar_trabajo}
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          </a>
                        ) : (
                          <p className="text-gray-600">{job.lugar_trabajo}</p>
                        )}
                      </div>
                    )}
                    {job.horario && (
                      <div>
                        <h3 className="font-semibold mb-2">Horario</h3>
                        <p className="text-gray-600">{job.horario}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-8">
              {/* Apply Card - Modificada para mostrar estado */}
              <div className="bg-white rounded-2xl shadow-lg p-6 top-28">
                {canApply ? (
                  <>
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        ¿Interesado en esta posición?
                      </h3>
                      <p className="text-gray-600">
                        Postúlate ahora y forma parte de nuestro equipo
                      </p>
                    </div>

                    <button
                      onClick={() => setShowApplicationForm(true)}
                      className="w-full btn-primary py-3 text-lg font-semibold mb-4"
                    >
                      Postularme ahora
                    </button>

                    <div className="space-y-4">
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="h-5 w-5 text-green-500 mr-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>Proceso rápido y sencillo</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="h-5 w-5 text-green-500 mr-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>Respuesta en máximo 72 horas</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="h-5 w-5 text-green-500 mr-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>Seguimiento personalizado</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center mb-6">
                      <div
                        className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                          isJobExpired ? 'bg-red-100' : 'bg-yellow-100'
                        }`}
                      >
                        <ExclamationTriangleIcon
                          className={`h-8 w-8 ${
                            isJobExpired ? 'text-red-600' : 'text-yellow-600'
                          }`}
                        />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {jobStatus?.title}
                      </h3>
                      <p className="text-gray-600">{jobStatus?.message}</p>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="h-5 w-5 text-gray-400 mr-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.698-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                        <span>
                          {isJobExpired
                            ? 'Esta vacante ya no está recibiendo aplicaciones'
                            : 'Esta vacante no está recibiendo aplicaciones'}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="h-5 w-5 text-gray-400 mr-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.698-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                        <span>
                          {isJobExpired
                            ? 'El período de recepción de aplicaciones ha finalizado'
                            : 'Puede haber sido cerrada o pausada temporalmente'}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="h-5 w-5 text-gray-400 mr-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.698-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                        <span>Revisa nuestras otras vacantes disponibles</span>
                      </div>
                    </div>

                    <Link
                      to="/vacantes"
                      className="w-full inline-flex items-center justify-center btn-secondary py-3 text-lg font-semibold mt-4"
                    >
                      Ver vacantes disponibles
                    </Link>
                  </>
                )}
              </div>

              {/* Job Info Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-6">
                  {getLanguageText('jobInfo', language)}
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-gray-600">Fecha de posteo</span>
                    <span className="font-semibold">
                      {formatDate(job.fecha_posteo)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-gray-600">Ubicación</span>
                    <span className="font-semibold">
                      {job.localidad}, {job.estado}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-gray-600">Tipo de empleo</span>
                    <span className="font-semibold">{job.tipo_empleo}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-gray-600">Salario</span>
                    <span className="font-semibold">
                      ${job.salario} {job.moneda} {job.tipo_salario}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Vacante vence</span>
                    <span className="font-semibold">
                      {formatDate(job.finaliza_vacante)}
                    </span>
                  </div>
                  {/* Estado de la vacante */}
                  <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                    <span className="text-gray-600">Estado</span>
                    <span
                      className={`font-semibold ${
                        canApply
                          ? 'text-green-600'
                          : isJobExpired
                            ? 'text-red-600'
                            : 'text-yellow-600'
                      }`}
                    >
                      {canApply
                        ? 'Disponible'
                        : isJobExpired
                          ? 'Vencida'
                          : 'Inactiva'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Company Info Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-6">
                  {getLanguageText('companyInfo', language)}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-primary-blue/10 rounded-lg mr-4">
                      <img src="/favicon.png" alt="Packsys" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Packsys</h4>
                      <p className="text-gray-600 text-sm">
                        Distribución de productos de empaque y embalaje
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <a
                      href="https://packsys.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-primary-blue hover:text-primary-orange"
                    >
                      <GlobeAltIcon className="h-5 w-5 mr-3" />
                      <span>https://packsys.com/</span>
                    </a>
                    <a
                      href="mailto:unete@packsys.com"
                      className="flex items-center text-primary-blue hover:text-primary-orange"
                    >
                      <EnvelopeIcon className="h-5 w-5 mr-3" />
                      <span>unete@packsys.com</span>
                    </a>
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="font-semibold mb-3">Contacto directo</h4>
                    <div className="space-y-2">
                      <a
                        href="https://wa.me/525588693920?text=¡Hola%20me%20gustaría%20ser%20parte%20del%20equipo%20Packsys!"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                      >
                        <svg
                          className="h-5 w-5 mr-2"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.76.982.998-3.675-.236-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.9 6.994c-.004 5.45-4.437 9.88-9.885 9.88m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.333.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.333 11.893-11.893 0-3.18-1.24-6.162-3.495-8.411" />
                        </svg>
                        WhatsApp CDMX
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Share Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-4">Compartir vacante</h3>
                <div className="flex justify-between space-x-4">
                  <div className="flex flex-1 btn-secondary items-center">
                    <ClipboardDocumentIcon className="h-6 w-6 mr-2" />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        alert('Enlace copiado al portapapeles');
                      }}
                    >
                      Copiar enlace
                    </button>
                  </div>
                  <div className="flex flex-1 btn-secondary items-center">
                    <FaLinkedinIn className="h-6 w-6 mr-2" />
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                        window.location.href
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Back to Jobs */}
          <div className="mt-12 text-center">
            <Link
              to="/vacantes"
              className="inline-flex items-center text-primary-blue hover:text-primary-orange font-medium text-lg"
            >
              <ArrowLeftIcon className="h-6 w-6 mr-2" />
              Ver todas las vacantes disponibles
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default JobDetailsPage;
